/**
 * The unit at a glance: your own team, and the rest of the roster.
 *
 * Two kinds of information, so two cards. Your team's rows carry a live online
 * count, from the aircraft streaming to this screen. Other teams' rows carry
 * their roster size only: their aircraft reach you as map positions, and a
 * count of "online" built from that would read as their status when it is only
 * what the map happens to show. A user in every team or in none gets the one
 * roster card, since a "Your team" heading over the whole list labels nothing.
 *
 * Each team's port block is on its row, because which ports a team owns is the
 * one thing an operator needs in order to connect, and nowhere else on the
 * Command screen said it.
 *
 * The list scrolls inside a bounded card instead of cutting the roster at a
 * "+N more", and the bound is a whole number of rows so the cut never falls
 * through the middle of a team's port line.
 */

import { ChevronDown, ChevronUp, Plane, Radio, Shield, Users } from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { describePorts, listTeams } from '@/api/teams';
import { useCurrentUser } from '@/stores/authStore';
import { useDroneStore } from '@/stores/droneStore';
import type { DroneId, Team } from '@/types/domain';

/** Matches the row height below. Five whole rows show before the list scrolls. */
const ROW_PX = 46;
const VISIBLE_ROWS = 5;
const REFRESH_MS = 15_000;

interface Props {
  /** Which team each aircraft belongs to, from /api/teams/drone-map. */
  droneTeams: Record<DroneId, { teamId: string }>;
}

/** Online and total aircraft per team, counted only from the caller's own. */
function useOwnTeamCounts(
  droneTeams: Record<DroneId, { teamId: string }>,
): Record<string, number> {
  return useDroneStore(
    useShallow((state) => {
      const online: Record<string, number> = {};
      for (const [id, drone] of state.drones) {
        if (drone.access === 'map' || drone.state !== 'connected') continue;
        const teamId = droneTeams[id]?.teamId;
        if (teamId) online[teamId] = (online[teamId] ?? 0) + 1;
      }
      return online;
    }),
  );
}

function onlineColor(online: number, total: number): string {
  if (total === 0 || online === 0) return 'var(--muted-foreground)';
  return online >= total ? 'var(--ok)' : 'var(--caution)';
}

function TeamRow({
  team,
  mine,
  online,
}: {
  team: Team;
  mine: boolean;
  online: number;
}): ReactNode {
  const ports = describePorts(team);
  const total = team.droneCount;
  return (
    <div
      className="flex flex-col justify-center gap-0.5 border-b border-[color:var(--border-nested)] px-3 last:border-b-0"
      style={{ minHeight: ROW_PX }}
      data-testid={`team-row-${team.callsign}`}
    >
      <div className="flex items-center gap-2">
        <span className="h-3 w-[3px] shrink-0 rounded-full" style={{ background: team.color }} />
        <span className="label-mono text-[10px]" style={{ color: team.color }}>
          {team.callsign}
        </span>
        <span className="min-w-0 flex-1 truncate text-[11px] text-[color:var(--muted-foreground-3)]">
          {team.displayName}
        </span>
        {mine ? (
          <span
            className="tnum inline-flex items-center gap-1 text-[10.5px]"
            style={{ color: onlineColor(online, total) }}
            title={`${online} of ${total} aircraft online`}
          >
            <Radio size={11} aria-hidden />
            {online}/{total}
          </span>
        ) : (
          <span
            className="tnum inline-flex items-center gap-1 text-[10.5px] text-[color:var(--muted-foreground)]"
            title={`${total} aircraft on their roster. Another team, so their status is not shown here.`}
          >
            <Plane size={11} aria-hidden />
            {total}
          </span>
        )}
        <span
          className="tnum inline-flex items-center gap-1 text-[10.5px] text-[color:var(--muted-foreground)]"
          title={`${team.memberCount ?? 0} members`}
        >
          <Users size={11} aria-hidden />
          {team.memberCount ?? 0}
        </span>
      </div>
      {ports.length ? (
        <div
          className="tnum pl-[11px] font-mono text-[9.5px] text-[color:var(--muted-foreground)]"
          title="Point a link at any port in this range and the aircraft joins this team"
        >
          {ports.join('  ·  ')}
        </div>
      ) : null}
    </div>
  );
}

function Card({
  title,
  icon,
  summary,
  accent,
  open,
  onToggle,
  children,
}: {
  title: string;
  icon: ReactNode;
  summary: ReactNode;
  accent: boolean;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}): ReactNode {
  return (
    <section
      className="overflow-hidden rounded-[12px] border bg-[oklch(1_0_0/0.03)]"
      style={{
        borderColor: accent
          ? 'color-mix(in oklch, var(--friend) 45%, transparent)'
          : 'var(--border-nested)',
      }}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full items-center gap-2 px-3 py-2 text-left"
        style={open ? { borderBottom: '1px solid var(--border-nested)' } : undefined}
      >
        {icon}
        <span className="label-mono text-[9px]">{title}</span>
        <span className="ml-auto flex items-center gap-2">{summary}</span>
        {open ? <ChevronUp size={13} aria-hidden /> : <ChevronDown size={13} aria-hidden />}
      </button>
      {open ? (
        <div
          className="scroll-thin overflow-y-auto"
          // Whole rows only, and never more than a third of the screen, so a
          // short laptop still has room for the aircraft list below.
          style={{ maxHeight: `min(${ROW_PX * VISIBLE_ROWS}px, 33vh)` }}
        >
          {children}
        </div>
      ) : null}
    </section>
  );
}

export function TeamsPanel({ droneTeams }: Props): ReactNode {
  const user = useCurrentUser();
  const [teams, setTeams] = useState<Team[]>([]);
  const [mineOpen, setMineOpen] = useState(true);
  const [othersOpen, setOthersOpen] = useState(true);
  const online = useOwnTeamCounts(droneTeams);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      const result = await listTeams();
      if (alive && result.ok) setTeams(result.data);
    };
    void load();
    const timer = setInterval(() => void load(), REFRESH_MS);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, []);

  const myIds = useMemo(() => new Set(user?.teamIds ?? []), [user]);
  const mine = teams.filter((t) => myIds.has(t.teamId));
  const others = teams.filter((t) => !myIds.has(t.teamId));

  if (teams.length === 0) return null;

  const row = (team: Team) => (
    <TeamRow
      key={team.teamId}
      team={team}
      mine={myIds.has(team.teamId)}
      online={online[team.teamId] ?? 0}
    />
  );

  if (mine.length === 0 || others.length === 0) {
    const aircraft = teams.reduce((n, t) => n + t.droneCount, 0);
    return (
      <Card
        title="TEAMS"
        icon={<Shield size={13} aria-hidden className="text-[color:var(--friend)]" />}
        summary={
          <span className="tnum inline-flex items-center gap-1 text-[10px] text-[color:var(--muted-foreground)]">
            <Plane size={11} aria-hidden />
            {aircraft}
          </span>
        }
        accent={mine.length > 0}
        open={mineOpen}
        onToggle={() => setMineOpen((v) => !v)}
      >
        {teams.map(row)}
      </Card>
    );
  }

  const mineTotal = mine.reduce((n, t) => n + t.droneCount, 0);
  const mineOnline = mine.reduce((n, t) => n + (online[t.teamId] ?? 0), 0);
  const othersTotal = others.reduce((n, t) => n + t.droneCount, 0);

  return (
    <div className="flex flex-col gap-1.5">
      <Card
        title={mine.length === 1 ? 'YOUR TEAM' : 'YOUR TEAMS'}
        icon={<Shield size={13} aria-hidden className="text-[color:var(--friend)]" />}
        summary={
          <span
            className="tnum inline-flex items-center gap-1 text-[10px]"
            style={{ color: onlineColor(mineOnline, mineTotal) }}
            title={`${mineOnline} of ${mineTotal} of your aircraft online`}
          >
            <Radio size={11} aria-hidden />
            {mineOnline}/{mineTotal}
          </span>
        }
        accent
        open={mineOpen}
        onToggle={() => setMineOpen((v) => !v)}
      >
        {mine.map(row)}
      </Card>
      <Card
        title="OTHER TEAMS"
        icon={<Users size={13} aria-hidden className="text-[color:var(--muted-foreground)]" />}
        summary={
          <span
            className="tnum inline-flex items-center gap-1 text-[10px] text-[color:var(--muted-foreground)]"
            title={`${others.length} teams, ${othersTotal} aircraft on their rosters`}
          >
            <Plane size={11} aria-hidden />
            {othersTotal}
          </span>
        }
        accent={false}
        open={othersOpen}
        onToggle={() => setOthersOpen((v) => !v)}
      >
        {others.map(row)}
      </Card>
    </div>
  );
}

export default TeamsPanel;
